import type { ProgramInput } from './mm-program-schema';

type RulesInput = NonNullable<ProgramInput['rules']>;
export type OutOfBookInput = NonNullable<RulesInput['out_of_book']>;
export type VolatilityLadder = Pick<NonNullable<RulesInput['volatility']>, 'rungs_bps' | 'add_bps'>;

/*
 * The scoring job's formulas, so the editor can draw what a setting pays. Keep them in step with mm-program:
 * `src/backtest/pay.py` (payout_fraction, qualifying_seconds, out_of_book_cut) and `src/backtest/ladder.py`.
 */

/** Share of compensation a run earns with a bucket met for `timeAtKpi` % of it (pay.py `payout_fraction`). */
export const payoutFraction = (timeAtKpi: number, threshold: number, exponentStart?: number | null, exponent?: number | null): number => {
  if (timeAtKpi >= threshold) return 1;
  if (threshold <= 0 || exponent === undefined || exponent === null) return 0;
  const start = exponentStart ?? 0;
  const span = threshold - start;
  if (span <= 0) return 0;
  const scaled = (timeAtKpi - start) / span;
  return scaled <= 0 ? 0 : scaled ** exponent;
};

/** The least time at KPI (%) that earns `payout` (0–1) of compensation: `payoutFraction` inverted. */
export const timeAtKpiForPayout = (payout: number, threshold: number, exponentStart?: number | null, exponent?: number | null): number => {
  const start = exponentStart ?? 0;
  if (threshold <= 0 || exponent === undefined || exponent === null || threshold <= start) return threshold;
  return exponent === 0 ? start : start + (threshold - start) * payout ** (1 / exponent);
};

/** Pay cut for a run with one out-of-book gap of `gapSeconds`: only a gap longer than `min_gap_s` counts, in full. */
export const outOfBookCut = (gapSeconds: number, penalty: OutOfBookInput): number => {
  const counted = gapSeconds > penalty.min_gap_s ? gapSeconds : 0;
  return counted <= 0 ? 0 : Math.min(penalty.max_cut_fraction, (penalty.max_cut_fraction * counted) / penalty.full_s);
};

/** Width added to B1 and B2 once the 60s EWMA rises above `ewmaBps`: the ladder sits on the highest rung it is strictly above. */
export const volatilityWideningAbove = (ewmaBps: number, ladder: VolatilityLadder): number => {
  const level = ladder.rungs_bps.filter((rung) => rung <= ewmaBps).length;
  return level ? (ladder.add_bps[level - 1] ?? 0) : 0;
};
